import React, { useState, useEffect } from 'react';
import { Package } from 'lucide-react';
import api from '../../lib/api';
import ProductCard from '../ProductCard';
import { mapCmsFilterToApiParams, getPageConfig } from '../../lib/pageConfig';

/**
 * ============================================================================
 * ProductListBlock - Bloc CMS pour afficher une liste de produits
 * ============================================================================
 * 
 * DESIGN SYSTEM CONTRACT:
 * ✅ width: 100% du parent (le layout contrôle la largeur)
 * ✅ padding interne configurable
 * ✅ styles internes (grid layout)
 * ❌ INTERDIT: max-width basé sur viewport
 * ❌ INTERDIT: width en % du viewport
 * ❌ INTERDIT: margin auto pour contrôler la largeur globale
 * 
 * RÈGLE: Le LAYOUT parent définit la largeur, pas le bloc.
 * 
 * ARCHITECTURE CONFORME:
 * - Query Engine: Filtres sur dates ISO uniquement (via mapCmsFilterToApiParams)
 * - State Engine: États is_* calculés par le backend
 * - Frontend: Affichage uniquement via ProductCard
 * 
 * SUPPORT PAGES THÉMATIQUES:
 * - filter.pageType = 'nouveautes' → Nouveautés en cours (J-30 à J)
 * - filter.pageType = 'nouveautes-a-venir' → Nouveautés à venir (J à J+30 + null)
 * - filter.pageType = 'offres' → Offres ponctuelles
 * - filter.pageType = 'precommandes' → Précommandes actives
 * 
 * Features héritées de ProductCard:
 * - Boutons Favori / À suivre
 * - Info jeu (joueurs, âge, durée)
 * - Partner cliquable
 * - Tags avec styling unifié (via State Engine)
 */
const ProductListBlock = ({ block }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { filter = {}, limit = 8, layout, title, subtitle } = block.content;
  
  // Récupérer la config de page thématique si définie
  const pageConfig = filter.pageType ? getPageConfig(filter.pageType) : null;

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filter)]);

  const loadProducts = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // === ARCHITECTURE CONFORME ===
      // Utilise mapCmsFilterToApiParams pour convertir les filtres CMS en paramètres API
      // Supporte les filtres de dates ISO pour les pages thématiques
      const apiParams = mapCmsFilterToApiParams(filter);
      
      const params = new URLSearchParams();
      Object.entries(apiParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(key, value);
        }
      });
      params.append('limit', limit);

      console.log('🔍 [ProductListBlock] Loading products with params:', params.toString());
      
      const response = await api.get(`/products?${params.toString()}`);
      const rawBlock = response.data || [];
      let fetchedProducts = Array.isArray(rawBlock) ? rawBlock : (rawBlock?.products || []);
      
      // Tri côté frontend si nécessaire (le backend ne trie pas par date)
      if (pageConfig?.sort) {
        fetchedProducts = sortProducts(fetchedProducts, pageConfig.sort);
      }
      
      setProducts(fetchedProducts);
    } catch (error) {
      console.error('Error loading products:', error);
      setError('Impossible de charger les produits');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction de tri (appliqué après récupération)
  const sortProducts = (products, sortKey) => {
    const sorted = [...products];
    
    switch (sortKey) {
      case 'date_sortie_desc':
        // Plus récent d'abord, null à la fin
        return sorted.sort((a, b) => {
          if (!a.date_sortie_iso && !b.date_sortie_iso) return 0;
          if (!a.date_sortie_iso) return 1;
          if (!b.date_sortie_iso) return -1;
          return b.date_sortie_iso.localeCompare(a.date_sortie_iso);
        });
        
      case 'date_sortie_asc':
        // Plus proche d'abord, null à la fin
        return sorted.sort((a, b) => {
          if (!a.date_sortie_iso && !b.date_sortie_iso) return 0;
          if (!a.date_sortie_iso) return 1;
          if (!b.date_sortie_iso) return -1;
          return a.date_sortie_iso.localeCompare(b.date_sortie_iso);
        });
        
      case 'remise_desc':
        return sorted.sort((a, b) => (b.remise || 0) - (a.remise || 0));
        
      default:
        return sorted;
    }
  };

  const getGridClass = () => {
    if (!layout) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
    
    const desktop = layout.desktop?.replace('grid-', '');
    const tablet = layout.tablet?.replace('grid-', '');
    const mobile = layout.mobile?.replace('grid-', '');
    
    return `grid-cols-${mobile || 1} sm:grid-cols-${tablet || 2} lg:grid-cols-${desktop || 4}`;
  };
  
  // Titre et sous-titre (peut venir du bloc ou de la config de page)
  const displayTitle = title || pageConfig?.title;
  const displaySubtitle = subtitle || pageConfig?.subtitle;
  const emptyMessage = pageConfig?.ui?.emptyMessage || 'Aucun produit disponible';

  if (loading) {
    return (
      <div 
        className="product-list-block"
        data-testid="product-list-block-loading"
        style={{ width: '100%', boxSizing: 'border-box' }}
      >
        {displayTitle && <h2 className="text-2xl font-heading font-semibold mb-2">{displayTitle}</h2>}
        {displaySubtitle && <p className="text-muted-foreground mb-6">{displaySubtitle}</p>}
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className="product-list-block"
        data-testid="product-list-block-error"
        style={{ width: '100%', boxSizing: 'border-box' }}
      >
        {displayTitle && <h2 className="text-2xl font-heading font-semibold mb-2">{displayTitle}</h2>}
        <p className="text-center text-red-500 py-12">{error}</p>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div 
        className="product-list-block"
        data-testid="product-list-block-empty"
        style={{ width: '100%', boxSizing: 'border-box' }}
      >
        {displayTitle && <h2 className="text-2xl font-heading font-semibold mb-2">{displayTitle}</h2>}
        {displaySubtitle && <p className="text-muted-foreground mb-6">{displaySubtitle}</p>}
        <div className="text-center py-12">
          <Package className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="product-list-block"
      data-testid="product-list-block"
      data-block-type="product_list"
      style={{
        // ============================================================
        // DESIGN SYSTEM CONTRACT: width = 100% du parent
        // Le layout parent contrôle la largeur, pas ce composant
        // ============================================================
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      {displayTitle && <h2 className="text-2xl font-heading font-semibold mb-2">{displayTitle}</h2>}
      {displaySubtitle && <p className="text-muted-foreground mb-6">{displaySubtitle}</p>}
      <div className={`grid ${getGridClass()} gap-6`}>
        {products.map((product) => (
          <ProductCard 
            key={product.id} 
            product={product}
            variant="default"
            showTag={true}
            /* 
             * ARCHITECTURE CONFORME:
             * ProductCard utilise product.tags (générés par State Engine)
             * et product.is_* pour l'affichage des badges
             * Aucune logique métier ici
             */
          />
        ))}
      </div>
    </div>
  );
};

export default ProductListBlock;
